
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface ComparisonProduct {
  id: string;
  name: string;
  price: number;
  compare_at_price: number | null;
  image_url: string | null;
  description: string | null;
  stock_quantity: number | null;
  categories?: {
    name: string;
  };
  features?: Record<string, any>;
}

interface ComparisonState {
  items: ComparisonProduct[];
  isOpen: boolean;
}

interface ComparisonContextType extends ComparisonState {
  addToComparison: (product: ComparisonProduct) => void;
  removeFromComparison: (productId: string) => void;
  clearComparison: () => void;
  isInComparison: (productId: string) => boolean;
  toggleDrawer: () => void;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

type ComparisonAction =
  | { type: 'ADD_ITEM'; payload: ComparisonProduct }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_COMPARISON' }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'SET_COMPARISON'; payload: ComparisonProduct[] };

const comparisonReducer = (state: ComparisonState, action: ComparisonAction): ComparisonState => {
  switch (action.type) {
    case 'ADD_ITEM':
      if (state.items.length >= 4) {
        return state; // Max 4 items for comparison
      }
      if (state.items.find(item => item.id === action.payload.id)) {
        return state; // Already in comparison
      }
      const newItems = [...state.items, action.payload];
      return { ...state, items: newItems };
    
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'CLEAR_COMPARISON':
      return { ...state, items: [] };
    
    case 'TOGGLE_DRAWER':
      return { ...state, isOpen: !state.isOpen };
    
    case 'SET_COMPARISON':
      return { ...state, items: action.payload };
    
    default:
      return state;
  }
};

export const ComparisonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(comparisonReducer, {
    items: [],
    isOpen: false,
  });

  const { toast } = useToast();

  // Load comparison from localStorage on mount
  useEffect(() => {
    try {
      const savedComparison = localStorage.getItem('product_comparison');
      if (savedComparison) {
        const items = JSON.parse(savedComparison);
        dispatch({ type: 'SET_COMPARISON', payload: items });
      }
    } catch (error) {
      console.error('Failed to load comparison from localStorage:', error);
    }
  }, []);

  // Save to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem('product_comparison', JSON.stringify(state.items));
    } catch (error) {
      console.error('Failed to save comparison to localStorage:', error);
    }
  }, [state.items]);

  const addToComparison = (product: ComparisonProduct) => {
    if (state.items.length >= 4) {
      toast({
        title: "Comparison limit reached",
        description: "You can compare up to 4 products at a time.",
        variant: "destructive",
      });
      return;
    }

    if (state.items.find(item => item.id === product.id)) {
      toast({
        title: "Already in comparison",
        description: "This product is already in your comparison list.",
        variant: "destructive",
      });
      return;
    }

    dispatch({ type: 'ADD_ITEM', payload: product });
    toast({
      title: "Added to comparison",
      description: `${product.name} has been added to comparison.`,
    });
  };

  const removeFromComparison = (productId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
    toast({
      title: "Removed from comparison",
      description: "Product has been removed from comparison.",
    });
  };

  const clearComparison = () => {
    dispatch({ type: 'CLEAR_COMPARISON' });
    toast({
      title: "Comparison cleared",
      description: "All products have been removed from comparison.",
    });
  };

  const isInComparison = (productId: string) => {
    return state.items.some(item => item.id === productId);
  };

  const toggleDrawer = () => {
    dispatch({ type: 'TOGGLE_DRAWER' });
  };

  return (
    <ComparisonContext.Provider
      value={{
        ...state,
        addToComparison,
        removeFromComparison,
        clearComparison,
        isInComparison,
        toggleDrawer,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
};
